package com.checkin.host.ui.dashboard

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.api.ApiException
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.cardStyle
import com.checkin.host.ui.layout.AlertBanner
import com.checkin.host.ui.layout.PageHeader
import com.checkin.host.ui.layout.rememberIsMobile
import com.checkin.shared.dto.CalendarDayDto
import com.checkin.shared.dto.CalendarMonthDto
import com.checkin.shared.dto.DashboardStatsDto
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun DashboardScreen() {
    var stats by remember { mutableStateOf<DashboardStatsDto?>(null) }
    var calendar by remember { mutableStateOf<CalendarMonthDto?>(null) }
    var selectedDay by remember { mutableStateOf<CalendarDayDto?>(null) }
    var month by remember { mutableStateOf(currentMonthString()) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    val isMobile = rememberIsMobile()

    fun load() {
        scope.launch {
            loading = true
            error = null
            try {
                stats = ApiClient.dashboardStats()
                calendar = ApiClient.dashboardCalendar(month)
            } catch (e: ApiException) {
                error = e.message
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(month) { load() }

    PageHeader("Tableau de bord", "Vue d'ensemble de vos réservations")
    error?.let { AlertBanner(it, isError = true) }

    if (loading && stats == null) {
        P({ style { color(AppColors.muted) } }) { Text("Chargement...") }
        return
    }

    stats?.let { s ->
        Div({
            style {
                display(DisplayStyle.Grid)
                property(
                    "grid-template-columns",
                    if (isMobile) "1fr" else "repeat(auto-fit, minmax(180px, 1fr))",
                )
                gap(14.px)
                marginBottom(16.px)
            }
        }) {
            StatCard("Réservations en attente", s.pendingReservations.toString(), AppColors.warning)
            StatCard("Vérifications complètes", s.completedVerifications.toString(), AppColors.success)
            StatCard("Invités en cours", s.guestsInProgress.toString(), AppColors.info)
        }

        H3({
            style {
                fontSize(15.px)
                fontWeight("600")
                color(AppColors.textSub)
                margin(0.px, 0.px, 12.px, 0.px)
            }
        }) { Text("Statistiques") }

        Div({
            style {
                display(DisplayStyle.Grid)
                property(
                    "grid-template-columns",
                    if (isMobile) "1fr" else "repeat(auto-fit, minmax(180px, 1fr))",
                )
                gap(14.px)
                marginBottom(24.px)
            }
        }) {
            StatCard(
                label = "Taux d'occupation",
                value = "${s.occupancyRatePercent} %",
                accent = AppColors.primary,
                subtitle = formatMonthLabel(s.statsMonth),
            )
            StatCard(
                label = "Durée séjour moy.",
                value = formatStayDays(s.averageStayDays),
                accent = AppColors.text,
            )
            StatCard(
                label = "Check-ins ce mois",
                value = s.checkInsThisMonth.toString(),
                accent = AppColors.success,
                subtitle = formatMonthLabel(s.statsMonth),
            )
        }
    }

    calendar?.let { cal ->
        CalendarCard(
            month = month,
            days = cal.days,
            selectedDay = selectedDay,
            isMobile = isMobile,
            onPrevMonth = {
                month = shiftMonth(month, -1)
                selectedDay = null
            },
            onNextMonth = {
                month = shiftMonth(month, 1)
                selectedDay = null
            },
            onSelectDay = { selectedDay = it },
        )
    }
}

@Composable
private fun CalendarCard(  // Obsidian-themed calendar
    month: String,
    days: List<CalendarDayDto>,
    selectedDay: CalendarDayDto?,
    isMobile: Boolean,
    onPrevMonth: () -> Unit,
    onNextMonth: () -> Unit,
    onSelectDay: (CalendarDayDto) -> Unit,
) {
    Div({ style { cardStyle() } }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                marginBottom(16.px)
                gap(8.px)
            }
        }) {
            Button({ style { navBtn() }; onClick { onPrevMonth() } }) { Text("←") }
            H3({ style { margin(0.px); fontSize(if (isMobile) 16.px else 18.px); color(AppColors.text) } }) { Text(formatMonthLabel(month)) }
            Button({ style { navBtn() }; onClick { onNextMonth() } }) { Text("→") }
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(7, 1fr)")
                gap(if (isMobile) 2.px else 4.px)
                fontSize(if (isMobile) 10.px else 12.px)
                textAlign("center")
            }
        }) {
            listOf("Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim").forEach { label ->
                Div({ style { fontWeight("600"); color(AppColors.muted); padding(4.px) } }) { Text(label) }
            }

            val padding = firstWeekdayOffset(month)
            repeat(padding) {
                Div({ style { minHeight(if (isMobile) 30.px else 36.px) } }) {}
            }

            days.forEach { day ->
                val hasActivity = day.checkIns > 0 || day.checkOuts > 0
                val isSelected = selectedDay?.date == day.date
                Div({
                    style {
                        minHeight(if (isMobile) 30.px else 36.px)
                        padding(if (isMobile) 2.px else 4.px)
                        borderRadius(6.px)
                        cursor(if (hasActivity) "pointer" else "default")
                        backgroundColor(
                            when {
                                isSelected -> Color(AppColors.calendarSelectedBg)
                                hasActivity -> AppColors.calendarActivityBg
                                else -> Color.transparent
                            },
                        )
                        border(
                            if (isSelected) 1.px else 0.px,
                            LineStyle.Solid,
                            AppColors.primary,
                        )
                    }
                    if (hasActivity) onClick { onSelectDay(day) }
                }) {
                    Div({
                        style {
                            fontWeight(if (hasActivity) "600" else "400")
                            color(if (hasActivity) AppColors.text else AppColors.muted)
                        }
                    }) { Text(day.date.substringAfterLast("-")) }
                    if (day.checkIns > 0) {
                        Div({ style { fontSize(9.px); color(AppColors.success) } }) { Text("↓${day.checkIns}") }
                    }
                    if (day.checkOuts > 0) {
                        Div({ style { fontSize(9.px); color(AppColors.warning) } }) { Text("↑${day.checkOuts}") }
                    }
                }
            }
        }

        selectedDay?.let { day ->
            Div({
                style {
                    marginTop(16.px)
                    padding(12.px)
                    backgroundColor(AppColors.surfaceHigh)
                    borderRadius(8.px)
                    fontSize(14.px)
                    border(1.px, LineStyle.Solid, AppColors.border)
                    color(AppColors.textSub)
                }
            }) {
                Text("${day.date} — ${day.checkIns} arrivée(s), ${day.checkOuts} départ(s)")
            }
        }

        P({ style { fontSize(12.px); color(AppColors.muted); marginTop(12.px); marginBottom(0.px) } }) {
            Text("↓ check-in · ↑ check-out")
        }
    }
}

@Composable
private fun StatCard(label: String, value: String, accent: CSSColorValue, subtitle: String? = null) {
    Div({
        style {
            cardStyle()
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(8.px)
            property("position", "relative")
            property("overflow", "hidden")
        }
    }) {
        Div({
            style {
                fontSize(38.px)
                fontWeight("700")
                color(accent)
                lineHeight("1")
                letterSpacing((-1).px)
            }
        }) { Text(value) }
        Div({
            style {
                fontSize(13.px)
                color(AppColors.muted)
                fontWeight("500")
            }
        }) { Text(label) }
        subtitle?.let { hint ->
            Div({
                style {
                    fontSize(11.px)
                    color(AppColors.muted)
                    property("opacity", "0.85")
                }
            }) { Text(hint) }
        }
        // Decorative glow dot
        Div({
            style {
                property("position", "absolute")
                property("top", "-20px")
                property("right", "-20px")
                width(80.px)
                height(80.px)
                borderRadius(50.percent)
                property("opacity", "0.08")
                backgroundColor(accent)
                property("pointer-events", "none")
            }
        })
    }
}

private fun StyleScope.navBtn() {
    backgroundColor(AppColors.surfaceHigh)
    border(1.px, LineStyle.Solid, AppColors.border)
    borderRadius(8.px)
    padding(8.px, 12.px)
    cursor("pointer")
    color(AppColors.textSub)
}

private fun formatStayDays(days: Double): String {
    if (days <= 0.0) return "—"
    val rounded = kotlin.math.round(days * 10.0) / 10.0
    val text = rounded.toString().replace('.', ',')
    return "$text j"
}

private fun currentMonthString(): String {
    val d = js("new Date()").unsafeCast<dynamic>()
    val y = d.getFullYear() as Int
    val m = (d.getMonth() as Int) + 1
    return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}"
}

private fun formatMonthLabel(month: String): String {
    val parts = month.split("-")
    if (parts.size != 2) return month
    val names = listOf("", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre")
    val m = parts[1].toIntOrNull() ?: return month
    return "${names.getOrElse(m) { parts[1] }} ${parts[0]}"
}

private fun shiftMonth(month: String, delta: Int): String {
    val parts = month.split("-")
    var y = parts[0].toInt()
    var m = parts[1].toInt() + delta
    while (m < 1) { m += 12; y -= 1 }
    while (m > 12) { m -= 12; y += 1 }
    return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}"
}

private fun firstWeekdayOffset(month: String): Int {
    val parts = month.split("-")
    if (parts.size != 2) return 0
    val year = parts[0].toIntOrNull() ?: return 0
    val monthNum = parts[1].toIntOrNull() ?: return 0
    val sundayZero = weekdaySundayZero(year, monthNum, 1)
    return if (sundayZero == 0) 6 else sundayZero - 1
}

private fun weekdaySundayZero(year: Int, month: Int, day: Int): Int {
    val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    var y = year
    if (month < 3) y -= 1
    return (y + y / 4 - y / 100 + y / 400 + t[month - 1] + day) % 7
}
